import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useRecoilValue } from 'recoil';
import { enqueueSnackbar } from '~/utils/notifications';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { BooleanOperation } from '~/engine/util/algorithms/sdf/SDFOperations';
import {
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { environmentState } from '~/state/atoms/engineAtoms';
import {
    domainEventBus,
    RoiCreationRequestedEvent,
    RoiNameChangeRequestedEvent,
    RoiDeletionRequestedEvent,
} from '~/domain/events';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { createStructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import {
    OperationPayload,
    OperationStep,
    StepPayload,
} from '~/engine/util/algorithms/sdf/boolean-operations/types';
import {
    pollUntil,
    buildFormulaText as buildFormulaTextUtil,
    buildPayload as buildPayloadUtil,
    buildPayloadFromFormula as buildPayloadFromFormulaUtil,
} from '~/engine/util/algorithms/sdf/boolean-operations/booleanOperationUtils';
import {
    computeBooleanPreviewTexture,
    executeBooleanOperations,
} from '~/engine/util/algorithms/sdf/boolean-operations/booleanOperationExecution';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { getPreviewColor } from '~/components/toolbar/OperationsToolbar/margin/marginToolbarUtils';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import {
    validateROIAndTexture,
    validateStructureSetWritable,
} from '~/engine/util/algorithms/sdf/boolean-operations/validationUtils';
import { validateStructureName } from '~/utils/validation';
import {
    generateUniqueStructureNameFromList,
    STRUCTURE_NAME_MAX_LENGTH,
} from '~/domain/services/dicomProcessor';
import { usePreviewBase } from '~/components/toolbar/OperationsToolbar/shared/usePreviewBase';

const BOOLEAN_SUFFIX = '_bool';

export function useBooleanOperation() {
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const environment = useRecoilValue(environmentState);
    const selectedROI = useRecoilValue(selectedROIState);
    const session = useSessionOrNull();

    // Computed once here; all state initializers and effects use structures[0]
    // so point ROIs (markers, isocenters, registration points) are excluded
    // in a single place.
    const structures = useMemo(
        () => currentStructureSet?.roiArray.filter((r) => !r.isPointRoi) || [],
        [currentStructureSet]
    );

    const isStructureSetApproved = currentStructureSet?.isApproved ?? false;

    // All ROI names including point ROIs — used for uniqueness validation so
    // new structure names cannot clash with existing markers or isocenters.
    const allRoiNames = useMemo(
        () => currentStructureSet?.roiArray.map((r) => r.name) ?? [],
        [currentStructureSet?.roiArray]
    );

    const [steps, setSteps] = useState<OperationStep[]>(() => {
        const selected =
            selectedROI >= 0
                ? structures.find((r) => r.roiNumber === selectedROI)
                : undefined;
        const defaultLeftOperand = selected ?? structures[0] ?? null;
        return [
            {
                id: uuidv4(),
                leftOperand: defaultLeftOperand,
                rightOperands: [],
                operation: BooleanOperation.OR,
                collapsed: false,
            },
        ];
    });
    const [resultName, setResultName] = useState('');
    // null = create a new structure; a roiNumber = write result into that
    // existing ROI. The result target defaults to "new structure" (null) and
    // an existing ROI is targeted only when the user explicitly picks one,
    // keeping the non-destructive (create) path the default and consistent
    // with the Margin and Ring operations.
    const [resultTargetRoiNumber, setResultTargetRoiNumber] = useState<
        number | null
    >(null);

    // Sync the first step's leftOperand (the operand picker) with the
    // viewport-selected ROI. Operands are read-only sources, so an approved
    // structure is allowed here; the result target is intentionally not synced
    // (it stays at its default until the user explicitly picks one).
    useEffect(() => {
        if (!structures.length) {
            return;
        }

        const selected =
            selectedROI >= 0
                ? structures.find((r) => r.roiNumber === selectedROI)
                : undefined;
        const target = selected ?? structures[0] ?? null;

        setSteps((prev) => {
            if (!prev.length) {
                return prev;
            }
            const [firstStep, ...rest] = prev;
            if (firstStep.leftOperand?.roiNumber === target?.roiNumber) {
                return prev;
            }
            return [{ ...firstStep, leftOperand: target }, ...rest];
        });
    }, [structures, selectedROI]);

    const patient = useMemo(
        () =>
            patientRepository
                .getAll()
                .find((p) => p.dataSet.PatientID === selectedPatientId),
        [patientRepository, selectedPatientId]
    );

    const findROI = (roiNumber: number): RegionOfInterest | null => {
        return environment?.getRoi(roiNumber) || null;
    };

    // Default name for a new structure: first operand name + suffix, unique
    // across all existing ROIs.
    const firstOperandName = steps[0]?.leftOperand?.name;
    const defaultResultName = useMemo(() => {
        if (!firstOperandName) {
            return '';
        }
        const maxBase = STRUCTURE_NAME_MAX_LENGTH - BOOLEAN_SUFFIX.length;
        const baseName = `${firstOperandName.substring(0, maxBase)}${BOOLEAN_SUFFIX}`;
        return generateUniqueStructureNameFromList(
            structures.map((s) => s.name),
            baseName
        );
    }, [firstOperandName, structures]);

    const isValid = useMemo(() => {
        // When creating a new structure, validate the name if explicitly set.
        // An empty name is valid — the auto-generated default will be used.
        if (resultTargetRoiNumber === null) {
            const trimmedName = resultName.trim();
            if (trimmedName) {
                // Validate against all ROIs (including point ROIs) — structure
                // names must be unique across the entire structure set.
                const existingNames =
                    currentStructureSet?.roiArray.map((r) => r.name) ?? [];
                const [nameValid] = validateStructureName(
                    trimmedName,
                    existingNames
                );
                if (!nameValid) {
                    return false;
                }
            } else if (!defaultResultName) {
                // No operand selected yet — can't generate a default name.
                return false;
            }
        }

        return steps.every((step, index) => {
            const hasRightOperands = step.rightOperands.length > 0;
            if (index === 0) {
                // First step requires an explicit left operand selected by the user.
                return Boolean(step.leftOperand) && hasRightOperands;
            }

            // Steps after the first use the previous step's result as their
            // left operand (set to 'previous-result' at build time), so only
            // the right operands need to be filled in by the user.
            return hasRightOperands;
        });
    }, [
        resultTargetRoiNumber,
        steps,
        resultName,
        defaultResultName,
        currentStructureSet?.roiArray,
    ]);

    const buildPayload = useCallback((): OperationPayload | null => {
        if (!patient || !currentStructureSet) {
            return null;
        }
        // When writing into an existing ROI the result name is the target
        // ROI's own name — the user doesn't need to supply one.
        // When creating a new ROI, fall back to the auto-generated default if
        // the user left the name field empty.
        let effectiveResultName = resultName.trim() || defaultResultName;
        if (resultTargetRoiNumber !== null) {
            const targetRoi = currentStructureSet.roiArray.find(
                (r) => r.roiNumber === resultTargetRoiNumber
            );
            if (!targetRoi) {
                enqueueSnackbar('Target structure not found', {
                    variant: 'error',
                });
                return null;
            }
            effectiveResultName = targetRoi.name;
        }
        return buildPayloadUtil(
            steps,
            effectiveResultName,
            isValid,
            patient,
            currentStructureSet
        );
    }, [
        patient,
        currentStructureSet,
        resultName,
        defaultResultName,
        resultTargetRoiNumber,
        steps,
        isValid,
    ]);

    const operationText = useMemo(() => {
        if (!patient || !currentStructureSet) {
            return '';
        }

        return buildFormulaTextUtil(steps, patient, currentStructureSet);
    }, [patient, steps, currentStructureSet]);

    const buildPayloadFromFormula = (
        formulaText: string
    ): OperationPayload | null => {
        if (!patient || !currentStructureSet) {
            enqueueSnackbar('No patient or structure set loaded', {
                variant: 'error',
            });
            return null;
        }
        return buildPayloadFromFormulaUtil(
            formulaText,
            resultName,
            patient,
            currentStructureSet
        );
    };

    const resolveRightOperands = (
        step: StepPayload
    ): RegionOfInterest[] | null => {
        const rightROIs: RegionOfInterest[] = [];
        for (const rightRef of step.rightOperands) {
            const rightROI = findROI(rightRef.roi.roiNumber);
            const validation = validateROIAndTexture(
                rightROI,
                rightRef.roi.name
            );
            if (!validation.valid) {
                enqueueSnackbar(validation.error, { variant: 'error' });
                return null;
            }
            rightROIs.push(rightROI!);
        }
        return rightROIs;
    };

    /**
     * Converts an OperationPayload (with StructureReferences) to an OperationPayload<RegionOfInterest>
     * (with RegionOfInterest objects). Validates all ROIs before conversion.
     */
    const convertToExecutionPayload = (
        payload: OperationPayload
    ): OperationPayload<RegionOfInterest> | null => {
        if (payload.steps.length === 0) {
            return null;
        }

        const executionSteps: StepPayload<RegionOfInterest>[] = [];

        for (let i = 0; i < payload.steps.length; i++) {
            const step = payload.steps[i]!;

            if (i === 0) {
                if (step.leftOperand === 'previous-result') {
                    enqueueSnackbar(
                        'Invalid operation: first step cannot use previous result',
                        { variant: 'error' }
                    );
                    return null;
                }

                const sourceROI = findROI(step.leftOperand.roi.roiNumber);
                const sourceValidation = validateROIAndTexture(
                    sourceROI,
                    step.leftOperand.roi.name
                );
                if (!sourceValidation.valid) {
                    enqueueSnackbar(sourceValidation.error, {
                        variant: 'error',
                    });
                    return null;
                }

                const rightROIs = resolveRightOperands(step);
                if (!rightROIs) {
                    return null;
                }
                executionSteps.push({
                    leftOperand: sourceROI!,
                    rightOperands: rightROIs,
                    operation: step.operation,
                });
            } else {
                const rightROIs = resolveRightOperands(step);
                if (!rightROIs) {
                    return null;
                }
                executionSteps.push({
                    leftOperand: 'previous-result',
                    rightOperands: rightROIs,
                    operation: step.operation,
                });
            }
        }

        return {
            steps: executionSteps,
            resultName: payload.resultName,
            formulaText: payload.formulaText,
        };
    };

    const {
        beginOperation: beginPreviewOperation,
        setResult: publishPreview,
        cleanup: clearBooleanPreview,
    } = usePreviewBase({ environment });

    // Hold the latest `convertToExecutionPayload` in a ref so the
    // useCallback'd `previewOperation` reads the current closure state
    // without having to list a per-render-fresh function in its deps.
    const convertToExecutionPayloadRef = useRef(convertToExecutionPayload);
    convertToExecutionPayloadRef.current = convertToExecutionPayload;

    const previewOperation = useCallback(
        async (payload: OperationPayload): Promise<void> => {
            if (!currentStructureSet || !environment) {
                enqueueSnackbar('Environment not ready', { variant: 'error' });
                return;
            }

            if (!environment.image) {
                enqueueSnackbar('No image loaded — cannot display preview', {
                    variant: 'error',
                });
                return;
            }

            const signal = beginPreviewOperation();

            try {
                const executionPayload =
                    convertToExecutionPayloadRef.current(payload);
                if (!executionPayload) {
                    return;
                }

                // Yield to the browser before heavy GPU computation so the UI
                // can paint (e.g. show a loading state) — matches margin/ring pattern.
                await waitForUI();

                if (signal.aborted) {
                    return;
                }

                const previewTexture =
                    computeBooleanPreviewTexture(executionPayload);
                if (!previewTexture) {
                    enqueueSnackbar('Could not compute preview', {
                        variant: 'warning',
                    });
                    return;
                }

                if (signal.aborted) {
                    previewTexture.delete();
                    return;
                }

                // Use the first source ROI's color for the preview overlay.
                const firstStep = executionPayload.steps[0];
                const sourceColor =
                    firstStep.leftOperand !== 'previous-result'
                        ? firstStep.leftOperand.color
                        : ([1, 1, 1] as [number, number, number]);
                const previewColor = getPreviewColor(sourceColor);

                const previewObj = new CustomTargetObject(
                    environment.image,
                    previewTexture,
                    previewColor
                );
                previewObj.renderOptions = { fillAlpha: 0.18 };

                publishPreview(previewObj);
            } catch (error) {
                console.error('Preview operation failed:', error);
                enqueueSnackbar(`Preview failed: ${getErrorMessage(error)}`, {
                    variant: 'error',
                });
            }
        },
        [
            currentStructureSet,
            environment,
            beginPreviewOperation,
            publishPreview,
        ]
    );

    const executeOperation = async (
        payload: OperationPayload
    ): Promise<void> => {
        if (!currentStructureSet || !environment) {
            enqueueSnackbar('Environment not ready', { variant: 'error' });
            return;
        }

        const structureSetValidation =
            validateStructureSetWritable(currentStructureSet);
        if (!structureSetValidation.valid) {
            enqueueSnackbar(structureSetValidation.error, {
                variant: 'error',
            });
            return;
        }

        // --- Path A: write result into an existing ROI ---
        if (resultTargetRoiNumber !== null) {
            const targetReadModel = currentStructureSet.roiArray.find(
                (r) => r.roiNumber === resultTargetRoiNumber
            );
            if (!targetReadModel) {
                enqueueSnackbar('Target structure not found', {
                    variant: 'error',
                });
                return;
            }

            if (targetReadModel.isApproved) {
                enqueueSnackbar(
                    `Cannot modify approved structure "${targetReadModel.name}"`,
                    { variant: 'error' }
                );
                return;
            }

            const targetROI = findROI(resultTargetRoiNumber);
            if (!targetROI) {
                enqueueSnackbar('Target structure not found in engine', {
                    variant: 'error',
                });
                return;
            }

            try {
                const executionPayload = convertToExecutionPayload(payload);
                if (!executionPayload) {
                    return;
                }

                const success = executeBooleanOperations(
                    executionPayload,
                    targetROI
                );
                if (!success) {
                    enqueueSnackbar('Boolean operation execution failed', {
                        variant: 'error',
                    });
                    return;
                }

                // Warn if the operation cleared the destination ROI (e.g. AND
                // with no overlap), mirroring the empty-result check in Path B.
                if (
                    !targetROI.texture ||
                    targetROI.isEmpty ||
                    targetROI.volumeCm3 === 0
                ) {
                    environment.triggerContourLayerUpdate();
                    enqueueSnackbar(
                        `Boolean operation produced an empty result in "${targetReadModel.name}"`,
                        {
                            variant: 'warning',
                        }
                    );
                    return;
                }

                environment.triggerContourLayerUpdate();
                enqueueSnackbar(
                    `Applied to "${targetReadModel.name}" successfully`,
                    { variant: 'success' }
                );
            } catch (error) {
                console.error('Boolean operation failed:', error);
                enqueueSnackbar(
                    `Boolean operation failed: ${getErrorMessage(error)}`,
                    { variant: 'error' }
                );
            }
            return;
        }

        // --- Path B: create a new ROI ---
        let newRoiModel: { roiNumber: number } | null = null;
        try {
            const structureSet =
                session?.patient.getStructureSetWithSopInstanceUid(
                    currentStructureSet.dicomUid
                );
            if (!structureSet) {
                enqueueSnackbar(
                    `Structure set not found: ${currentStructureSet.dicomUid}`,
                    { variant: 'error' }
                );
                return;
            }

            // Create new ROI and wait for it
            await domainEventBus.publish(
                new RoiCreationRequestedEvent(
                    currentStructureSet.dicomUid,
                    'user'
                )
            );

            newRoiModel = await pollUntil(() => {
                const model = createStructureSetReadModel(structureSet);
                if (
                    model.roiArray.length > currentStructureSet.roiArray.length
                ) {
                    return model.roiArray[model.roiArray.length - 1];
                }
                return null;
            }, 5000);

            if (!newRoiModel) {
                enqueueSnackbar('Failed to create new ROI: timeout', {
                    variant: 'error',
                });
                return;
            }

            // Set the result name and wait for it
            await domainEventBus.publish(
                new RoiNameChangeRequestedEvent(
                    newRoiModel.roiNumber,
                    payload.resultName,
                    'user'
                )
            );

            await pollUntil(() => {
                const model = createStructureSetReadModel(structureSet);
                const roi = model.roiArray.find(
                    (r) => r.roiNumber === newRoiModel!.roiNumber
                );
                return roi?.name === payload.resultName ? roi : null;
            }, 1000);

            // Get the RegionOfInterest from the engine
            const resultROI = findROI(newRoiModel!.roiNumber);
            if (!resultROI) {
                enqueueSnackbar('Failed to get result ROI from engine', {
                    variant: 'error',
                });
                return;
            }

            // Convert payload to execution format and validate all ROIs
            const executionPayload = convertToExecutionPayload(payload);
            if (!executionPayload) {
                // Clean up: delete the ROI that was created since validation failed
                await domainEventBus.publish(
                    new RoiDeletionRequestedEvent(
                        currentStructureSet.dicomUid,
                        newRoiModel!.roiNumber,
                        'user'
                    )
                );
                return;
            }

            // Execute the boolean operations
            const success = executeBooleanOperations(
                executionPayload,
                resultROI
            );
            if (!success) {
                await domainEventBus.publish(
                    new RoiDeletionRequestedEvent(
                        currentStructureSet.dicomUid,
                        newRoiModel!.roiNumber,
                        'user'
                    )
                );
                enqueueSnackbar('Boolean operation execution failed', {
                    variant: 'error',
                });
                return;
            }

            // If the operation produced an empty result, clean it up
            if (
                !resultROI.texture ||
                resultROI.isEmpty ||
                resultROI.volumeCm3 === 0
            ) {
                await domainEventBus.publish(
                    new RoiDeletionRequestedEvent(
                        currentStructureSet.dicomUid,
                        newRoiModel!.roiNumber,
                        'user'
                    )
                );
                enqueueSnackbar(
                    'Boolean operation produced an empty result (no volume)',
                    { variant: 'error' }
                );
                return;
            }

            environment.triggerContourLayerUpdate();
            enqueueSnackbar(`Created "${payload.resultName}" successfully`, {
                variant: 'success',
            });
        } catch (error) {
            console.error('Boolean operation failed:', error);
            const errorMessage = `Boolean operation failed: ${getErrorMessage(error)}`;

            if (newRoiModel) {
                try {
                    await domainEventBus.publish(
                        new RoiDeletionRequestedEvent(
                            currentStructureSet.dicomUid,
                            newRoiModel.roiNumber,
                            'user'
                        )
                    );
                } catch (deleteError) {
                    console.error(
                        'Failed to clean up ROI after error:',
                        deleteError
                    );
                }
            }

            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    return {
        steps,
        setSteps,
        resultName,
        setResultName,
        resultTargetRoiNumber,
        setResultTargetRoiNumber,
        structures,
        isStructureSetApproved,
        allRoiNames,
        defaultResultName,
        isValid,
        buildPayload,
        buildPayloadFromFormula,
        previewOperation,
        clearBooleanPreview,
        executeOperation,
        operationText,
    };
}
